import { ProductCarousel } from '@johnlewispartnership/wtr-content-component-library';
import type { CmsProductCarouselProps } from '@johnlewispartnership/wtr-content-component-library';
import { useFeatureFlags } from '@johnlewispartnership/wtr-website-decisioner-client/express';

import { useFavouritesService } from 'services/favourites/useFavouritesService';
import { useTrolleyService } from 'services/trolley/useTrolleyService';
import { AEM_CONTENT_RENDERER_CLIENT_ENABLE_PRODUCT_CAROUSEL } from 'constants/feature-flags';
import { usePreloadedData } from 'renderer/context/preloadedDataContext';
import { buildListContext, type CarouselItem } from 'renderer/utils/analytics';
import { useFavouritesAnalytics } from 'renderer/hooks/useFavouritesAnalytics';

const ProductCarouselWrapper = ({
  items,
  ...rest
}: CmsProductCarouselProps & { productSource?: string }) => {
  const titleText = rest.title?.blocks[0]?.children?.[0]?.text.trim();
  const productSource = rest.productSource;
  const enableProductCarousel =
    useFeatureFlags()[AEM_CONTENT_RENDERER_CLIENT_ENABLE_PRODUCT_CAROUSEL];

  const { conflicts } = usePreloadedData();

  const getConflict = (item: CarouselItem) =>
    conflicts?.find(conflict => conflict.productId === item.searchProduct.id);

  const itemsWithAvailability = items.map(item => ({
    ...item,
    searchProduct: {
      ...item.searchProduct,
      availability: getConflict(item as CarouselItem)?.outOfStock ? 'sold out online' : 'available',
    },
  }));

  const { favourites, toggleFavourite } = useFavouritesService();
  const { trolley, isReady, addTrolleyItem, updateTrolleyItem } = useTrolleyService();
  const { trackAdd, trackRemove } = useFavouritesAnalytics(
    itemsWithAvailability as CarouselItem[],
    buildListContext(productSource, titleText),
  );

  if (!enableProductCarousel) {
    return null;
  }

  const enrichedItems = items.map(item => {
    const trolleyMatchedItem = trolley?.trolleyItems?.find(
      trolleyItem => trolleyItem.lineNumber === item.searchProduct.lineNumber,
    );

    return {
      ...item,
      searchProduct: {
        ...item.searchProduct,
        favourite: !!favourites[item.searchProduct.lineNumber || ''],
      },
      trolleyQuantity: trolleyMatchedItem?.quantity,
    };
  });

  const handleFavouriteClick = (lineNumber: string) => {
    if (favourites[lineNumber]) {
      trackRemove(lineNumber);
    } else {
      trackAdd(lineNumber);
    }

    toggleFavourite(lineNumber);
  };

  return (
    <ProductCarousel
      {...rest}
      items={enrichedItems}
      onFavouriteClick={handleFavouriteClick}
      onAddToTrolley={isReady ? addTrolleyItem : undefined}
      onUpdateTrolleyQuantity={isReady ? updateTrolleyItem : undefined}
      conflicts={conflicts}
    />
  );
};

export default ProductCarouselWrapper;
